import { Line, Polygon, Svg, Text as PdfText } from '@react-pdf/renderer';
import { sv } from 'date-fns/locale';
import React from 'react';

import { downloadFileUtils } from '../../../../../../services/axios/utility/download-file';
import { formattedDate } from '../../../../../../utils/dateUtils';
import { normalizeString } from '../../../../../../utils/text/text';
import {
  SVGLineData,
  SVGPoiData,
  SVGPointData,
  SVGTextData,
  SVGTriangleData,
} from '../../../../../../utils/three/vectorize-geometry';
import { projectStateAdapter } from '../../../../../state/project-state/project-state-adapter';
import { CadDrawingReportImage } from './cad-drawing-image-utils';
import { CadDrawingVectorized } from './cad-drawing-pdf';
import { InfoPanelOptions } from './cad-drawing-pdf-report-menu';
import { PoiMarker } from './poi-marker';

/**
 * Export vectorized CAD drawing to PDF
 */
export async function exportVectorizedCadDrawing(
  lines: SVGLineData[],
  triangles: SVGTriangleData[],
  texts: SVGTextData[],
  points: SVGPointData[],
  pois: SVGPoiData[],
  width: number,
  height: number,
  options: InfoPanelOptions,
  backgroundImage: CadDrawingReportImage,
  backgroundColor?: string,
): Promise<void> {
  try {
    // Validate dimensions
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      throw new Error(`Invalid dimensions: width=${width}, height=${height}`);
    }

    const defaultColor = backgroundColor === '#000000' ? 'white' : 'black';
    // Create SVG elements for the PDF
    const svgLines = lines.map((line, index) => (
      <Line
        key={`line-${index}`}
        stroke={line.color || defaultColor}
        strokeWidth={options.strokeWidth}
        x1={line.x1}
        x2={line.x2}
        y1={line.y1}
        y2={line.y2}
      />
    ));

    // Create SVG triangle elements for the PDF
    const svgTriangles = triangles.map((triangle, index) => (
      <Polygon
        fill={triangle.fill || 'none'}
        key={`triangle-${index}`}
        points={`${triangle.x1},${triangle.y1} ${triangle.x2},${triangle.y2} ${triangle.x3},${triangle.y3}`}
        stroke={triangle.color || defaultColor}
        strokeWidth={options.strokeWidth}
      />
    ));

    // Create react-pdf Text elements for the PDF (react-pdf doesn't support SVG text)
    const pdfTexts = texts.map((text, index) => {
      // fontWeight is now normalized to 'bold' | 'normal' in vectorize-geometry
      const fontWeight = text.fontWeight || 'normal';

      // Calculate rotation transform.
      const rotationStyle =
        text.rotation && Math.abs(text.rotation) > 0.01
          ? {
              transform: `rotate(${text.rotation * (180 / Math.PI)}deg)`,
              transformOrigin: `${text.x}px ${text.y}px`,
            }
          : {};

      return (
        <PdfText
          dominantBaseline={text.dominantBaseline || 'auto'}
          fill={text.color || defaultColor}
          key={`text-${index}`}
          style={{
            fontSize: text.fontSize,
            fontWeight,
            fontFamily: 'Inter24p',
            fontStyle: text.fontStyle,
            ...rotationStyle,
          }}
          textAnchor={text.textAnchor || 'start'}
          x={text.x}
          y={text.y}
        >
          {text.text}
        </PdfText>
      );
    });

    // Create SVG square elements for points (using Polygon for squares)
    const svgPoints = points.map((point, index) => {
      const size = options.strokeWidth * 1.15;
      const halfSize = size / 2;
      // Create square polygon with 4 corners
      const squarePoints = [
        `${point.x - halfSize},${point.y - halfSize}`, // top-left
        `${point.x + halfSize},${point.y - halfSize}`, // top-right
        `${point.x + halfSize},${point.y + halfSize}`, // bottom-right
        `${point.x - halfSize},${point.y + halfSize}`, // bottom-left
      ].join(' ');

      return (
        <Polygon fill={point.color || defaultColor} key={`point-${index}`} points={squarePoints} />
      );
    });

    // Filter POI markers - only include those without thumbnailUrl
    const poisWithoutThumbnails = pois.filter((poi) => !poi.thumbnailUrl);
    const poisWithThumbnails = pois.filter((poi) => poi.thumbnailUrl);

    // Create POI marker components for POIs without thumbnails
    const poiMarkers = poisWithoutThumbnails.map((poi, index) => (
      <PoiMarker key={`poi-${index}`} poi={poi} preview={false} />
    ));

    // Create the PDF document
    const doc = (
      <CadDrawingVectorized
        backgroundColor={backgroundColor}
        backgroundImage={backgroundImage}
        height={height}
        options={options}
        poisWithThumbnails={poisWithThumbnails}
        preview={false}
        svg={
          <Svg
            height={'100%'}
            preserveAspectRatio="xMidYMid meet"
            viewBox={`0 0 ${width} ${height}`}
            width={'100%'}
          >
            {svgTriangles}
            {svgLines}
            {svgPoints}
            {pdfTexts}
            {poiMarkers}
          </Svg>
        }
        width={width}
      />
    );

    // Generate PDF blob
    await downloadFileUtils.downloadReactPdfDocument(doc, {
      title: normalizeString(
        `${projectStateAdapter.getSnapshot().project!.name}_${formattedDate(Date.now(), sv)}.pdf`,
      ),
    });
  } catch (error) {
    console.error('Failed to export vectorized CAD drawing:', error);
    throw new Error('Vectorized CAD drawing export failed');
  }
}
